import { UnknownError } from '@omnea-digital/toolbox'
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { getPollingGCTime } from '@omnea-app/fe-api-client/queryConfig'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RequestDetail } from '@omnea-app/requests/requestsFrontendClient'
import { RequestResponse, RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'

export const RequestDetailQueryKey = 'GetRequestDetail'

export type GetRequestResponse = RequestResponse<'/{id}', 'get'>

export const fetchRequestDetails = async (token: string, requestId: string): Promise<RequestDetail> => {
  const apiClient = new RequestsHttpClient({ authorization: token })

  const { data, error } = await apiClient.GET('/{id}', {
    params: { path: { id: requestId } },
  })

  throwIfApiError(error)
  return data as RequestDetail
}

export const useGetRequestDetail = <TData = GetRequestResponse['data']>(
  requestId: string | null,
  options?: Omit<
    UseQueryOptions<GetRequestResponse['data'], GetRequestResponse['error'], TData>,
    'queryKey' | 'gcTime' | 'queryFn' | 'staleTime' | 'refetchInterval'
  > & { refetchInterval?: number | false }
) => {
  const { getToken } = useAuthenticatedUser()

  return useQuery<GetRequestResponse['data'], GetRequestResponse['error'], TData>({
    ...options,
    queryKey: [RequestDetailQueryKey, requestId],
    gcTime: getPollingGCTime(options?.refetchInterval),
    queryFn: async () => {
      if (!isDefined(requestId)) throw new UnknownError({ message: 'No requestID provided in fetch' })

      return await fetchRequestDetails(await getToken(), requestId)
    },
    // We call this hook from a few different places, so want to make sure that we don't
    // fire off a request for every single caller if the first one has already returned
    staleTime: 5_000,
  })
}
export const useRequestDetail = (
  requestId: string | null,
  { refetchInterval, skip }: { refetchInterval?: number | false; skip?: boolean } = {}
) => {
  const { user } = useAuthenticatedUser()

  return useGetRequestDetail(requestId, {
    refetchInterval,
    enabled: !user.isSupplier && isUuid(requestId) && !skip,
  }) as UseQueryResult<RequestDetail, Error>
}
